import {
  Box,
  HStack,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FiChevronDown } from "react-icons/fi";
import * as yup from "yup";
import ProceedCancelButtons from "components/Button/ProceedCancelButtons";
import { FloatingField } from "design-system";
import SwitchField from "components/FormElements/Fields/SwitchField";
import { Path } from "features/Path/models";
import { useV2PathListQuery } from "features/Path/hooks";
import {
  DEFAULT_ICON,
  DEFAULT_ICON_COLOR,
  PATH_ICON_COLOR_KEYS,
  PATH_ICON_KEYS,
  suggestIconForName,
} from "features/Path/icons";
import IconPicker from "../PathsList/IconPicker";
import IconTile from "../PathsList/IconTile";

const schema = yup.object({
  name: yup.string().label("Path Name (unique & recognizable)").required(),
  autoAdd: yup.boolean().label("Auto-Assign Path to All New Hires?").required(),
  icon: yup.string().oneOf([...PATH_ICON_KEYS]).required(),
  iconColor: yup.string().oneOf([...PATH_ICON_COLOR_KEYS]).required(),
});

type PathFormSchema = yup.InferType<typeof schema>;

interface PathFormContentProps {
  buttonText: string;
  path?: Path;
  duplicate?: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (update: PathFormSchema) => void;
  usedIcons?: Set<string>;
}

export default function PathFormContent({
  path,
  duplicate = false,
  onClose,
  onSubmit,
  isSubmitting,
  buttonText,
  usedIcons: externalUsedIcons,
}: PathFormContentProps) {
  const {
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    getValues,
  } = useForm<PathFormSchema>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      name: duplicate ? `${path?.name} (Copy)` : path?.name || "",
      // Duplicates are always manual — auto-trigger should never be silently inherited.
      autoAdd: duplicate ? false : path?.autoAdd || false,
      icon: path?.icon || DEFAULT_ICON,
      iconColor: path?.iconColor || DEFAULT_ICON_COLOR,
    },
  });

  // If the user hasn't touched the picker, propose an icon based on the name.
  // Once they choose an icon manually, stop overriding it.
  const pickerTouched = useRef<boolean>(!!path?.icon);
  const watchedName = useWatch({ control, name: "name" });
  const watchedIcon = useWatch({ control, name: "icon" });
  const watchedColor = useWatch({ control, name: "iconColor" });

  // Hoist stable RHF refs so the suggestion effect's dep array only reacts to real changes.
  const setValueRef = useRef(setValue);
  const getValuesRef = useRef(getValues);
  useEffect(() => {
    setValueRef.current = setValue;
    getValuesRef.current = getValues;
  }, [setValue, getValues]);

  const { data: allPaths } = useV2PathListQuery("", { enabled: externalUsedIcons === undefined });
  const usedIcons = useMemo(() => {
    if (externalUsedIcons !== undefined) return externalUsedIcons;
    const icons = new Set(allPaths?.map((p) => p.icon).filter(Boolean) as string[]);
    // Don't block the current path's own icon when editing (not duplicating).
    if (path && !duplicate) icons.delete(path.icon);
    return icons;
  }, [externalUsedIcons, allPaths, path, duplicate]);

  useEffect(() => {
    if (pickerTouched.current) return;
    const suggestion = suggestIconForName(watchedName, usedIcons);
    if (!suggestion) return;
    if (suggestion.icon === getValuesRef.current("icon") && suggestion.color === getValuesRef.current("iconColor")) return;
    setValueRef.current("icon", suggestion.icon, { shouldDirty: true });
    setValueRef.current("iconColor", suggestion.color, { shouldDirty: true });
  }, [watchedName, usedIcons]);

  return (
    <Stack spacing="4">
      <HStack spacing="3" align="end">
        <Popover placement="bottom-start" gutter={6}>
          <PopoverTrigger>
            <Box
              as="button"
              type="button"
              borderRadius="md"
              display="inline-flex"
              flexShrink={0}
              transition="opacity 120ms"
              _hover={{ opacity: 0.85 }}
              sx={{
                outline: "none",
                "&:focus-visible": {
                  outline: "2px solid var(--chakra-colors-cdsIndigo-500)",
                  outlineOffset: "1px",
                },
              }}
              aria-label="Pick a path icon"
            >
              <IconTile icon={watchedIcon} color={watchedColor} size={44} />
            </Box>
          </PopoverTrigger>
          <PopoverContent w="360px">
            <PopoverArrow />
            <PopoverBody p="4">
              <IconPicker
                icon={watchedIcon}
                color={watchedColor}
                previewName={watchedName}
                onChange={({ icon, color }) => {
                  pickerTouched.current = true;
                  setValue("icon", icon, { shouldDirty: true });
                  setValue("iconColor", color, { shouldDirty: true });
                }}
              />
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <Box flex="1">
          <FloatingField name="name" errors={errors.name?.message} required>
            <FloatingField.Label>Path Name (unique &amp; recognizable)</FloatingField.Label>
            <FloatingField.Text control={control} name="name" />
          </FloatingField>
        </Box>
      </HStack>
      <Text fontSize="xs" color="gray.500" pl="60px" mt="-2">
        Click the icon to change it
        {" "}
        <Icon as={FiChevronDown} boxSize={3} mb="-1px" />
      </Text>

      {!duplicate && (
        <SwitchField
          label="Auto-Assign Path to All New Hires?"
          name="autoAdd"
          helperText="Automatically assign this path to all new hires added to Camino."
          sideLayout
          errors={errors}
          control={control}
        />
      )}
      <HStack mt={12} justify="end">
        <ProceedCancelButtons
          handleSubmit={handleSubmit(onSubmit)}
          onClose={onClose}
          proceedText={buttonText}
          {...(isSubmitting !== undefined ? { isLoading: isSubmitting } : {})}
        />
      </HStack>
    </Stack>
  );
}
